import styled from 'styled-components';
import { ClickAwayListener } from '../../Common/ClickAwayListener';
import * as React from 'react';
import { JRCIcon } from '../../../JRCIcon/JRCIcon';
import { JRCLoader } from '../../../JRCLoader/JRCLoader';
import { JRCFlexBox } from 'components/JRCFlex/JRCFlexBox';
import { DashedFocusBorder } from '../../../Common/DashedFocusBorder';

export const RelativeClickAwayListener = styled(ClickAwayListener)`
    position: relative;
    z-index: auto;
`;
export const OptionWrapper = styled.button<{ isSelected?: boolean; isFocused?: boolean }>`
    display: block;
    width: 100%;
    background: none;
    ${(props) => props.isSelected && 'font-weight: bolder'};
    ${(props) => props.isFocused && 'background-color: #F8F8FF'};
    min-height: 32px;
    line-height: 32px;
    padding: 0 16px;
    cursor: pointer;
    transition: all 0.2s;

    ${() => DashedFocusBorder({ color: 'grey5' })}

    :hover {
        background-color: #f8f8ff;
    }
`;

export const Option = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

export const NoOption = styled.div`
    min-height: 36px;
    line-height: 36px;
    text-align: center;
    font-style: italic;
    color: ${(props) => props.theme.color.grey4};
`;

export const OptionGroup = styled.div``;

export const OptionGroupHeader = styled.div`
    min-height: 32px;
    line-height: 32px;
    padding: 0 8px;
    background-color: #efefff;
`;
export const OptionGroupValues = styled.div``;
export const OptionMenu = styled.div.attrs((props) => ({
    ...props,
    className: `option-menu ${props.className || ''}`,
}))<{ sizeVariant?: 's' | 'md' }>`
    max-height: max(200px, 40vh);
    min-width: 250px;
    background-color: white;
    overflow: auto;
    position: absolute;
    width: 100%;
    z-index: 3;
    border: 1px solid #dadaf0;
    border-radius: 8px;
    margin-top: 4px;
    top: ${({ sizeVariant }) => (sizeVariant === 's' ? 30 : 42)}px;

    > .custom {
        border-top: 1px solid ${(props) => props.theme.color.grey3};
    }

    > .custom ~ .custom {
        border-top: none;
    }
`;
export const SelectedOptionWrapper = styled.div``;
export const SelectedOption = styled.button.attrs(({ readOnly }: { readOnly: boolean }) => ({
    type: 'button',
    readOnly,
}))`
    display: inline-block;
    border: none;
    vertical-align: middle;

    margin: 0 4px 4px 0;
    cursor: default;

    ${(props) =>
        !props.readOnly &&
        `
        cursor: pointer;
        
        :hover,
        :focus-visible {
            div {
                background-color: ${props.theme.color.orangeL80};

                i {
                    color: ${props.theme.color.orange};
                }
            }
        }
        `};
`;
export const JRCDefaultSelectedOption = styled.div`
    min-height: 24px;
    line-height: 24px;

    display: flex;
    align-items: center;

    padding: 4px 16px 4px 12px;
    border-radius: 4px;
    background-color: #f8f8ff;
    transition: all 0.2s;
`;

export const TimesIcon = styled.i.attrs(() => ({ className: 'react-icon icon-times' }))`
    margin: 0 8px;
    color: ${(props) => props.theme.color.grey2};
    transition: all 0.2s;
`;

export const IconButtonSearch = styled.div`
    display: flex;
    z-index: 2;
    position: absolute;
    left: 1px;
    bottom: 5px;

    & button {
        height: 38px !important;
    }
`;

export const InputSelectHierarchySearch = styled.div`
    display: block;
    position: relative;
    overflow-y: auto;
    overflow-x: clip;

    & input {
        padding: '8px 16px 8px 40px !important';
    }
`;

export const InputSelectCheckboxWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    gap: 8px;
`;

export const CheckboxLabelButton = styled.button`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    padding: 5.5px 0px;
    color: ${(props) => props.theme.color.secondary};
`;

export const InputSelectSearch = styled.div`
    position: relative;
`;

export const OptionSelectCell = styled.button<{ isSelected: boolean }>`
    width: 100%;
    height: 100%;
    cursor: pointer;
    padding: 0px 4px;
    ${(props) => props.isSelected && `background-color: ${props.theme.color.grey1};`}
`;

export const ModalContentInputSelectList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

export const ModalContentInputSelect = styled.div`
    & input {
        padding: 8px 16px 8px 40px !important;
    }
`;

export const FlexBoxTreeDescription = styled.p`
    margin-bottom: 10px;
`;

export const NoResult = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 8px;
`;

export const WrapperList = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    height: 100%;
`;

export const ModalContentList = styled.div`
    display: block;
    overflow: scroll;
    width: 100%;

    & table {
        & tbody {
            & td {
                height: 21px;
                padding: 0px;
            }

            & tr:hover {
                background-color: ${(props) => props.theme.color.grey0};
            }
        }
    }

    margin-bottom: -24px;
`;

export const NoOptionList = styled(JRCFlexBox)`
    margin-top: 24px;
`;

export const IconBefore = styled(JRCIcon)<{ left?: number; top?: number; height?: number }>`
    position: absolute;
    left: ${(props) => props?.left || '8'}px;
    top: ${(props) => props?.top || '10'}px;
    ${(props) => props?.height && `height: ${props.height}px;`}
`;

const AbsoluteButton = styled.button<{ disabled?: boolean; $sizeVariant?: 's' | 'md' }>`
    position: absolute;
    top: ${({ $sizeVariant }) => ($sizeVariant === 's' ? 5 : 10)}px;
    right: 10px;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const AbsoluteDivTimes = styled.div`
    position: absolute;
    top: 10px;
    right: 40px;
    cursor: pointer;
`;

export const TimesIndicator = ({ onClick }: { onClick?: () => void }) => {
    return (
        <AbsoluteDivTimes onClick={onClick}>
            <JRCIcon name="icon-times" color="grey2" />
        </AbsoluteDivTimes>
    );
};

export const DropdownIndicator = ({
    loading,
    open,
    onClick,
    sizeVariant,
    disabled,
}: {
    loading: boolean;
    open: boolean;
    onClick?: () => void;
    sizeVariant?: 's' | 'md';
    disabled?: boolean;
}) => {
    return (
        <AbsoluteButton type="button" onClick={onClick} disabled={disabled} $sizeVariant={sizeVariant}>
            {loading ? (
                <JRCLoader />
            ) : (
                <JRCIcon
                    name={`icon-angle-${open ? 'up' : 'down'}`}
                    color="grey2"
                    size={sizeVariant === 's' ? 15 : 20}
                />
            )}
        </AbsoluteButton>
    );
};
